import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import TopBar from '../components/TopBar';
import AircraftStatusReport from '../components/AircraftStatusReport';
import FlyAircraftModal from '../components/FlyAircraftModal';
import OnGroundMode from '../components/OnGroundMode';
import PostFlightMode from '../components/PostFlightMode';
import HistoricAircraftData from '../components/HistoricAircraftData';
import DocumentsFolderPage from '../components/DocumentsFolderPage';
import AdminPanel from '../components/AdminPanel';
import { getAircraft, getFlight, getFlights, getUpcomingFlights, subscribeToDataEvents } from '../services/api';
import { loadSessionView, saveSessionView } from '../utils/viewState';
import cockpitModeIcon from '../assets/icons/cockpitmode.svg';
import './DashboardPage.css';

const ACTIVE_FLIGHT_STORAGE_KEY = 'etlb_active_flight_state';
const DASHBOARD_MENU_ITEMS = new Set(['cockpit', 'briefing', 'historic', 'documents', 'admin']);
const BOOKED_FLIGHTS_INITIAL_RETRY_MS = 3000;
const saveActiveFlightState = (flightId, mode) => {
  if (!flightId) return;
  localStorage.setItem(ACTIVE_FLIGHT_STORAGE_KEY, JSON.stringify({
    flightId,
    mode,
    savedAt: new Date().toISOString()
  }));
};
const clearActiveFlightState = () => {
  localStorage.removeItem(ACTIVE_FLIGHT_STORAGE_KEY);
};
const sameId = (left, right) => {
  if (left == null || right == null) return false;
  return String(left) === String(right);
};
const ADMIN_ROLES = new Set(['admin', 'administrator', 'super_admin', 'superadmin']);
const isAdminRole = role => {
  const value = String(role || '').trim().toLowerCase();
  return ADMIN_ROLES.has(value) || value.includes('admin');
};
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const retryRequest = async (request, attempts = 3, delayMs = 250) => {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await request();
    } catch (err) {
      lastError = err;
      if (attempt < attempts - 1) await wait(delayMs);
    }
  }
  throw lastError;
};
const getEventAircraftId = event => (
  event?.aircraft_id || (event?.resource === 'aircraft' ? event?.resource_id : null)
);
const getEventFlightId = event => (
  event?.flight_id || (event?.resource === 'flights' ? event?.resource_id : null)
);
const isEventRelevantToContext = (event, aircraftId, flightId) => {
  const eventAircraftId = getEventAircraftId(event);
  const eventFlightId = getEventFlightId(event);

  if (eventAircraftId && aircraftId && sameId(eventAircraftId, aircraftId)) return true;
  if (eventFlightId && flightId && sameId(eventFlightId, flightId)) return true;

  return false;
};

const MenuCockpitIcon = () => (
  <img src={cockpitModeIcon} alt="" className="dashboard-menu-cockpit-icon app-icon app-icon-cockpitmode" />
);

const MenuHistoricIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 3.3a6.7 6.7 0 1 1-6.1 9.4H2.4a.8.8 0 0 1-.7-1.2l1.8-3a.8.8 0 0 1 1.3 0l1.8 3a.8.8 0 0 1-.7 1.2H5.8A4.9 4.9 0 1 0 10 5.1c-1 0-2 .3-2.8.9l-1-1.4A6.6 6.6 0 0 1 10 3.3Z" fill="currentColor" />
    <path d="M9.1 6.7h1.7v3.1l2.3 1.4-.9 1.5-3.1-1.9V6.7Z" fill="currentColor" />
  </svg>
);

const MenuFolderIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M2.7 5.3c0-1 .8-1.8 1.8-1.8h3.7l1.5 1.8h5.8c1 0 1.8.8 1.8 1.8v6.6c0 1-.8 1.8-1.8 1.8H4.5c-1 0-1.8-.8-1.8-1.8V5.3Z" fill="currentColor" />
    <path d="M2.7 7h14.6v1.6H2.7z" fill="#16181b" opacity=".35" />
  </svg>
);

const MenuAdminIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 2.8 16.2 5v4.4c0 3.7-2.4 6.5-6.2 7.8-3.8-1.3-6.2-4.1-6.2-7.8V5L10 2.8Z" fill="currentColor" />
    <path d="M9.1 6.3h1.8v4.1H9.1V6.3Zm0 5.2h1.8v1.8H9.1v-1.8Z" fill="#16181b" opacity=".55" />
  </svg>
);

const MenuSignOutIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M7 3.5h4.3v1.8H7a1 1 0 0 0-1 1v7.4a1 1 0 0 0 1 1h4.3v1.8H7a2.8 2.8 0 0 1-2.8-2.8V6.3A2.8 2.8 0 0 1 7 3.5Z" fill="currentColor" />
    <path d="m11 6.1 1.3-1.3 5.2 5.2-5.2 5.2-1.3-1.3 2.9-2.9H8.2V8.9H14l-3-2.8Z" fill="currentColor" />
  </svg>
);

const DashboardPage = () => {
  const { aircraft, logout, switchAircraft, user } = useAuth();
  const dashboardViewStorageKey = `etlb_dashboard_view_${user?.id || 'anonymous'}`;
  const restoredDashboardView = loadSessionView(dashboardViewStorageKey);
  const [showFlyModal, setShowFlyModal] = useState(false);
  const [activeFlight, setActiveFlight] = useState(null);
  const [aircraftActiveFlight, setAircraftActiveFlight] = useState(null);
  const [activeFlightMode, setActiveFlightMode] = useState('on_ground');
  const [activeFlightSource, setActiveFlightSource] = useState('server');
  const [flightRestoreChecked, setFlightRestoreChecked] = useState(false);
  const [panelDirection, setPanelDirection] = useState('none');
  const [panelTransitionKey, setPanelTransitionKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(() => (
    DASHBOARD_MENU_ITEMS.has(restoredDashboardView.activeMenuItem)
      ? restoredDashboardView.activeMenuItem
      : 'cockpit'
  ));
  const [bookedFlights, setBookedFlights] = useState([]);
  const [bookedFlightsLoading, setBookedFlightsLoading] = useState(false);
  const [bookedFlightsError, setBookedFlightsError] = useState('');
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [bookedFlightsRefreshSignal, setBookedFlightsRefreshSignal] = useState(0);
  const [aircraftLockChecking, setAircraftLockChecking] = useState(false);
  const restoreRunRef = useRef(0);
  const lastReconciledRefreshSignalRef = useRef(0);
  const bookedFlightsRef = useRef([]);
  const aircraftActiveFlightLookupRef = useRef(0);

  const clearCurrentFlightSession = useCallback(({ clearAircraftActiveFlight = true } = {}) => {
    clearActiveFlightState();
    setActiveFlight(null);
    if (clearAircraftActiveFlight) setAircraftActiveFlight(null);
    setActiveFlightSource('server');
    setActiveFlightMode('on_ground');
  }, []);

  const refreshSelectedAircraftActiveFlight = useCallback(async () => {
    if (!aircraft?.id) {
      setAircraftActiveFlight(null);
      return null;
    }

    const lookupId = aircraftActiveFlightLookupRef.current + 1;
    aircraftActiveFlightLookupRef.current = lookupId;

    try {
      const [onGroundRes, postFlightRes] = await Promise.all([
        getFlights({ aircraft_id: aircraft.id, status: 'in_progress', limit: 1 }),
        getFlights({ aircraft_id: aircraft.id, status: 'post_flight', limit: 1 })
      ]);
      const nextFlight = postFlightRes.data.flights?.[0] || onGroundRes.data.flights?.[0] || null;
      if (aircraftActiveFlightLookupRef.current === lookupId) {
        setAircraftActiveFlight(nextFlight);
      }
      return nextFlight;
    } catch (err) {
      console.error('Error checking aircraft active flight:', err);
      if (aircraftActiveFlightLookupRef.current === lookupId) {
        setAircraftActiveFlight(null);
      }
      return null;
    }
  }, [aircraft?.id]);

  const switchToFlightAircraft = useCallback(async (flight) => {
    if (!flight?.aircraft_id || sameId(aircraft?.id, flight.aircraft_id)) return;

    try {
      const res = await getAircraft(flight.aircraft_id);
      if (res.data.aircraft) {
        switchAircraft(res.data.aircraft);
        return;
      }
    } catch (err) {
      console.error('Error switching to active flight aircraft:', err);
    }

    if (flight.registration || flight.ac_type) {
      switchAircraft({
        id: flight.aircraft_id,
        registration: flight.registration || aircraft?.registration || '',
        ac_type: flight.ac_type || aircraft?.ac_type || '',
        flight_hours: flight.aircraft_flight_hours ?? flight.flight_hours ?? aircraft?.flight_hours ?? 0,
        flight_cycles: flight.aircraft_flight_cycles ?? flight.flight_cycles ?? aircraft?.flight_cycles ?? 0
      });
    }
  }, [aircraft, switchAircraft]);

  const reconcileActiveFlight = useCallback(async () => {
    const flightId = activeFlight?.id;
    if (!flightId) return;

    try {
      const res = await getFlight(flightId);
      const flight = res.data.flight;
      const status = String(flight?.status || '').toLowerCase();

      if (
        !flight ||
        ['completed', 'cancelled'].includes(status) ||
        (user?.id && !sameId(flight.user_id, user.id))
      ) {
        clearCurrentFlightSession();
        return;
      }

      await switchToFlightAircraft(flight);
      const nextMode = status === 'post_flight' ? 'post_flight' : 'on_ground';
      setActiveFlight(flight);
      setAircraftActiveFlight(flight);
      setActiveFlightSource('server');
      setActiveFlightMode(nextMode);
      saveActiveFlightState(flight.id, nextMode);
    } catch (err) {
      if (err.response?.status === 404) {
        clearCurrentFlightSession();
      }
    }
  }, [activeFlight?.id, clearCurrentFlightSession, switchToFlightAircraft, user?.id]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToDataEvents(event => {
      if (event?.isLocalClientMutation) {
        return;
      }

      if (event?.resource === 'upcoming_flights') {
        if (aircraft?.id && sameId(getEventAircraftId(event), aircraft.id)) {
          setBookedFlightsRefreshSignal(signal => signal + 1);
        }
        return;
      }

      if (!isEventRelevantToContext(event, aircraft?.id, activeFlight?.id)) {
        return;
      }

      setRefreshSignal(signal => signal + 1);
    }, {
      aircraftId: aircraft?.id,
      flightId: activeFlight?.id
    });

    return unsubscribe;
  }, [activeFlight?.id, aircraft?.id, user?.id]);

  useEffect(() => {
    if (!flightRestoreChecked) return;
    if (refreshSignal <= 0) return;
    if (lastReconciledRefreshSignalRef.current === refreshSignal) return;
    lastReconciledRefreshSignalRef.current = refreshSignal;
    reconcileActiveFlight();
  }, [flightRestoreChecked, reconcileActiveFlight, refreshSignal]);

  useEffect(() => {
    let cancelled = false;
    const runId = restoreRunRef.current + 1;
    restoreRunRef.current = runId;

    const restoreActiveFlight = async () => {
      setFlightRestoreChecked(false);

      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem(ACTIVE_FLIGHT_STORAGE_KEY) || 'null');
      } catch {
        saved = null;
      }

      if (!saved?.flightId && !aircraft?.id && !user?.id) {
        setActiveFlight(null);
        setFlightRestoreChecked(true);
        return;
      }

      try {
        let flight = null;

        if (saved?.flightId) {
          try {
            const res = await retryRequest(() => getFlight(saved.flightId));
            flight = res.data.flight;
          } catch (err) {
            if (err.response?.status === 404) clearActiveFlightState();
          }
        }

        if (
          flight &&
          user?.id &&
          !sameId(flight.user_id, user.id)
        ) {
          clearActiveFlightState();
          flight = null;
        }

        if (!flight && user?.id) {
          const [onGroundRes, postFlightRes] = await retryRequest(() => Promise.all([
            getFlights({ user_id: user.id, status: 'in_progress', limit: 1 }),
            getFlights({ user_id: user.id, status: 'post_flight', limit: 1 })
          ]));
          flight = postFlightRes.data.flights?.[0] || onGroundRes.data.flights?.[0] || null;
        }

        if (cancelled || restoreRunRef.current !== runId) return;

        const status = String(flight?.status || '').toLowerCase();
        if (!flight || ['completed', 'cancelled'].includes(status)) {
          clearCurrentFlightSession({ clearAircraftActiveFlight: false });
          setFlightRestoreChecked(true);
          return;
        }

        await switchToFlightAircraft(flight);
        if (cancelled || restoreRunRef.current !== runId) return;

        setActiveFlight(flight);
        setAircraftActiveFlight(flight);
        setActiveFlightSource('server');
        const restoredMode = saved?.mode === 'post_flight' || status === 'post_flight' ? 'post_flight' : 'on_ground';
        setActiveFlightMode(restoredMode);
        saveActiveFlightState(flight.id, restoredMode);
        setPanelDirection('none');
      } catch {
        // Keep stored state on transient API failures; the next reload can still restore it.
      } finally {
        if (!cancelled && restoreRunRef.current === runId) setFlightRestoreChecked(true);
      }
    };

    restoreActiveFlight();
    return () => {
      cancelled = true;
    };
  }, [aircraft?.id, clearCurrentFlightSession, switchToFlightAircraft, user?.id]);

  useEffect(() => {
    refreshSelectedAircraftActiveFlight();
  }, [refreshSelectedAircraftActiveFlight, activeFlight?.id, activeFlightMode, refreshSignal]);

  useEffect(() => {
    if (!aircraft?.id) {
      setBookedFlights([]);
      bookedFlightsRef.current = [];
      setBookedFlightsLoading(false);
      setBookedFlightsError('');
      return undefined;
    }

    let cancelled = false;
    let retryTimeoutId = null;

    const applyBookedFlights = (flights) => {
      bookedFlightsRef.current = flights;
      setBookedFlights(flights);
    };

    const clearBookedFlightsRetry = () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }
    };

    const scheduleBookedFlightsRetry = () => {
      clearBookedFlightsRetry();
      if (cancelled || bookedFlightsRef.current.length > 0) return;
      retryTimeoutId = setTimeout(() => {
        loadBookedFlights({ showLoading: false, allowRetry: true });
      }, BOOKED_FLIGHTS_INITIAL_RETRY_MS);
    };

    const loadBookedFlights = async ({ showLoading = false, allowRetry = true } = {}) => {
      const hasExistingFlights = bookedFlightsRef.current.length > 0;
      if (showLoading || !hasExistingFlights) {
        setBookedFlightsLoading(true);
      }

      try {
        const res = await getUpcomingFlights(aircraft.id);
        if (cancelled) return;

        const flights = Array.isArray(res.data.flights) ? res.data.flights : [];
        const cache = res.data.cache || {};
        const source = res.data.source || '';
        const retryableEmptyCache = flights.length === 0 && (
          cache.retryable ||
          source === 'local-fallback' ||
          source === 'error' ||
          source === 'cache-empty'
        );

        if (flights.length > 0) {
          applyBookedFlights(flights);
          setBookedFlightsError('');
          setBookedFlightsLoading(false);
          clearBookedFlightsRetry();
          return;
        }

        if (!retryableEmptyCache) {
          applyBookedFlights([]);
          setBookedFlightsError(cache.error || '');
          setBookedFlightsLoading(false);
          clearBookedFlightsRetry();
          return;
        }

        setBookedFlightsError('');
        setBookedFlightsLoading(!bookedFlightsRef.current.length);
        if (allowRetry) scheduleBookedFlightsRetry();
      } catch (err) {
        if (cancelled) return;
        const hasExisting = bookedFlightsRef.current.length > 0;
        if (!hasExisting) {
          setBookedFlights([]);
          setBookedFlightsError(err.response?.data?.message || 'Failed to load booked flights');
        } else {
          setBookedFlightsError('');
        }
        setBookedFlightsLoading(!hasExisting && allowRetry);
        if (!hasExisting && allowRetry) scheduleBookedFlightsRetry();
      }
    };

    loadBookedFlights({ showLoading: true, allowRetry: true });

    return () => {
      cancelled = true;
      clearBookedFlightsRetry();
    };
  }, [aircraft?.id, bookedFlightsRefreshSignal]);

  useEffect(() => {
    if (!flightRestoreChecked) return;

    if (activeFlight?.id) saveActiveFlightState(activeFlight.id, activeFlightMode);
  }, [activeFlight, activeFlightMode, flightRestoreChecked]);

  const isAdmin = isAdminRole(user?.role);
  const menuItems = [
    { id: 'cockpit', label: 'Cockpit Mode', icon: MenuCockpitIcon },
    { id: 'historic', label: 'Historic A/C Data', icon: MenuHistoricIcon },
    { id: 'documents', label: 'Documents Folder', icon: MenuFolderIcon },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin Panel', icon: MenuAdminIcon }] : [])
  ];

  const handleMenuSelection = (nextMenuItem) => {
    if (nextMenuItem === activeMenuItem) {
      setMenuOpen(false);
      return;
    }

    const navigationOrder = ['cockpit', 'briefing', 'historic', 'documents', 'admin'];
    const currentIndex = Math.max(0, navigationOrder.indexOf(activeMenuItem));
    const nextIndex = Math.max(0, navigationOrder.indexOf(nextMenuItem));
    setPanelDirection(nextIndex >= currentIndex ? 'forward' : 'back');
    setPanelTransitionKey(key => key + 1);
    setActiveMenuItem(nextMenuItem);
    setMenuOpen(false);
  };

  useEffect(() => {
    if (activeMenuItem === 'admin' && !isAdmin) {
      setActiveMenuItem('cockpit');
    }
  }, [activeMenuItem, isAdmin]);

  useEffect(() => {
    saveSessionView(dashboardViewStorageKey, { activeMenuItem });
  }, [activeMenuItem, dashboardViewStorageKey]);

  const handleFlightEnded = () => {
    clearCurrentFlightSession();
    setShowFlyModal(false);
    setActiveMenuItem('briefing');
    setPanelDirection('back');
    setPanelTransitionKey(k => k + 1);
    setFlightRestoreChecked(true);
    setRefreshSignal(signal => signal + 1);
  };

  const handleFlySuccess = (flight) => {
    const nextMode = flight?.status === 'post_flight' ? 'post_flight' : 'on_ground';
    saveActiveFlightState(flight?.id, nextMode);
    setShowFlyModal(false);
    setActiveMenuItem('cockpit');
    setActiveFlightMode(nextMode);
    setPanelDirection('forward');
    setPanelTransitionKey(k => k + 1);
    setActiveFlight(flight);
    setAircraftActiveFlight(flight);
    setActiveFlightSource('created');
  };

  const handleBackToAircraftReport = () => {
    setPanelDirection('back');
    setPanelTransitionKey(k => k + 1);
    setActiveMenuItem('briefing');

    const flightId = activeFlight?.id;
    if (!flightId) return;

    getFlight(flightId)
      .then(res => {
        const flight = res.data.flight;
        const status = String(flight?.status || '').toLowerCase();

        if (!flight || ['completed', 'cancelled'].includes(status)) {
          clearCurrentFlightSession();
          return;
        }

        const nextMode = status === 'post_flight' ? 'post_flight' : 'on_ground';
        setActiveFlight(flight);
        setAircraftActiveFlight(flight);
        setActiveFlightSource('server');
        setActiveFlightMode(nextMode);
        saveActiveFlightState(flight.id, nextMode);
      })
      .catch(err => {
        if (err.response?.status === 404) {
          clearCurrentFlightSession();
        }
      });
  };

  const handleMoveToPostFlight = (acceptedFlight = null) => {
    const nextFlight = acceptedFlight?.id ? acceptedFlight : activeFlight;
    saveActiveFlightState(nextFlight?.id, 'post_flight');
    setPanelDirection('forward');
    setPanelTransitionKey(k => k + 1);
    if (nextFlight?.id) {
      const updatedFlight = { ...nextFlight, status: 'post_flight' };
      setActiveFlight(updatedFlight);
      setAircraftActiveFlight(updatedFlight);
      setActiveFlightSource('server');
    }
    setActiveFlightMode('post_flight');
  };

  const handleReturnToOnGround = (revokedFlight = null) => {
    const nextFlight = revokedFlight?.id ? revokedFlight : activeFlight;
    saveActiveFlightState(nextFlight?.id, 'on_ground');
    setPanelDirection('back');
    setPanelTransitionKey(k => k + 1);
    if (nextFlight?.id) {
      const updatedFlight = { ...nextFlight, status: 'in_progress' };
      setActiveFlight(updatedFlight);
      setAircraftActiveFlight(updatedFlight);
      setActiveFlightSource('server');
    }
    setActiveFlightMode('on_ground');
  };

  const handleLogout = () => {
    clearActiveFlightState();
    logout();
  };

  const activeFlightForSelectedAircraft = activeFlight?.id && (!aircraft?.id || sameId(activeFlight.aircraft_id, aircraft.id))
    ? activeFlight
    : null;
  const selectedAircraftActiveFlight = aircraftActiveFlight?.id ? aircraftActiveFlight : activeFlightForSelectedAircraft;
  const ownActiveFlight = activeFlightForSelectedAircraft && (!user?.id || sameId(activeFlightForSelectedAircraft.user_id, user.id));
  const aircraftIsFlying = Boolean(selectedAircraftActiveFlight?.id);
  const activeFlightPilotName = selectedAircraftActiveFlight?.pilot_name || user?.full_name || user?.email || 'another user';
  const flyDisabledReason = aircraftIsFlying
    ? `Aircraft is currently flying - ${activeFlightPilotName}`
    : '';

  const handleOpenFlyModal = async () => {
    setShowFlyModal(true);
    setAircraftLockChecking(true);
    try {
      await refreshSelectedAircraftActiveFlight();
    } finally {
      setAircraftLockChecking(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <TopBar
        menuOpen={menuOpen}
        onMenuToggle={() => setMenuOpen(v => !v)}
        aircraftLocked={Boolean(activeFlight)}
        refreshSignal={refreshSignal}
      />
      <div className={`dashboard-menu-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)} />
      <aside className={`dashboard-menu-drawer ${menuOpen ? 'open' : ''}`} aria-hidden={!menuOpen}>
        <div className="dashboard-menu-head">
          <span className="dashboard-menu-title">Menu</span>
          <span className="dashboard-menu-head-icon" aria-hidden="true">≡</span>
        </div>

        <div className="dashboard-menu-items">
          {menuItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`dashboard-menu-item ${activeMenuItem === item.id ? 'active' : ''}`}
                onClick={() => handleMenuSelection(item.id)}
              >
                <span className="dashboard-menu-item-icon"><Icon /></span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="dashboard-menu-footer">
          <button type="button" className="dashboard-menu-signout" onClick={handleLogout}>
            <span className="dashboard-menu-item-icon"><MenuSignOutIcon /></span>
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
      <div className="dashboard-main">
        <div
          key={`${activeMenuItem}-${activeFlight ? `flight-${activeFlight.id}-${activeFlightMode}` : 'aircraft'}-${panelTransitionKey}`}
          className={`dashboard-view-panel dashboard-view-${panelDirection}`}
        >
          {!flightRestoreChecked ? (
            <div className="spinner" style={{ margin: 'auto' }} />
          ) : activeMenuItem === 'admin' && isAdmin ? (
            <AdminPanel onDataChanged={() => setRefreshSignal(signal => signal + 1)} />
          ) : activeMenuItem === 'historic' ? (
            <HistoricAircraftData refreshSignal={refreshSignal} />
          ) : activeMenuItem === 'documents' ? (
            <DocumentsFolderPage />
          ) : activeMenuItem === 'cockpit' && ownActiveFlight ? (
            activeFlightMode === 'post_flight' ? (
              <PostFlightMode
                flightId={activeFlight.id}
                initialFlight={activeFlight}
                allowInitialFlightFallback={activeFlightSource === 'created'}
                onBack={handleBackToAircraftReport}
                onRevoke={handleReturnToOnGround}
                onFlightEnded={handleFlightEnded}
                refreshSignal={refreshSignal}
              />
            ) : (
              <OnGroundMode
                flightId={activeFlight.id}
                initialFlight={activeFlight}
                allowInitialFlightFallback={activeFlightSource === 'created'}
                onBack={handleBackToAircraftReport}
                onAccepted={handleMoveToPostFlight}
                onFlightEnded={handleFlightEnded}
                refreshSignal={refreshSignal}
              />
            )
          ) : (
            <AircraftStatusReport
              aircraftId={aircraft?.id}
              onFlyAircraft={handleOpenFlyModal}
              flyDisabled={Boolean(flyDisabledReason)}
              flyDisabledReason={flyDisabledReason}
              flyDisabledUserName={activeFlightPilotName}
              refreshSignal={refreshSignal}
            />
          )}
        </div>
      </div>

      {showFlyModal && (
        <FlyAircraftModal
          bookedFlights={bookedFlights}
          bookedFlightsLoading={bookedFlightsLoading}
          bookedFlightsError={bookedFlightsError}
          aircraftLocked={aircraftIsFlying}
          aircraftLockChecking={aircraftLockChecking}
          aircraftLockedReason={flyDisabledReason}
          onClose={() => setShowFlyModal(false)}
          onSuccess={handleFlySuccess}
        />
      )}
    </div>
  );
};

export default DashboardPage;
